/**
 * PaymentModal — pin/contant registreren bij een afleverstop of zelf-halen.
 *
 * WbModal 'dialoog': bedrag mono groot (geld-kleur alleen op "open"),
 * ontvangen bedrag mono in een feitenvak, methode als WbToggleGroup, knoppenrij
 * onderin. Payload naar createTransaction ongewijzigd.
 */

import React, { useState } from 'react';
import { salesforceClient } from '../../lib/salesforce';
import { WbButton, WbFactRow, WbFlag, WbModal, WbToggleGroup } from '../wb';

// =============================================================================
// TYPES
// =============================================================================

type PaymentMethod = 'Pin' | 'Contant';

export interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  workOrderId: string;
  workOrderName: string;
  customerName: string;
  openAmount: number;
  onPaymentSuccess: (result: {
    totalTransactions: number;
    openAmount: number;
    isFullyPaid: boolean;
  }) => void;
  onRefreshRoute?: () => Promise<void>;
}

function euro(n: number): string {
  return `€${n.toFixed(2).replace('.', ',')}`;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function PaymentModal({
  isOpen,
  onClose,
  workOrderId,
  workOrderName,
  customerName,
  openAmount,
  onPaymentSuccess,
  onRefreshRoute,
}: PaymentModalProps) {
  // Form state
  const [paymentAmount, setPaymentAmount] = useState(openAmount > 0 ? openAmount.toFixed(2) : '');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('Pin');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setPaymentAmount(openAmount > 0 ? openAmount.toFixed(2) : '');
      setPaymentMethod('Pin');
      setError(null);
    }
  }, [isOpen, openAmount]);

  const handleProcessPayment = async () => {
    if (!workOrderId) return;

    const amount = parseFloat(paymentAmount.replace(',', '.'));
    if (isNaN(amount) || amount <= 0) {
      setError('Voer een geldig bedrag in');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const response = await salesforceClient.createTransaction({
        workOrderId,
        amount,
        paymentMethod,
      });

      if (response.success) {
        // Betaalvelden zitten in de ApiResponse-envelop onder `data`
        const result = response.data;
        onPaymentSuccess({
          totalTransactions: result?.totalTransactions ?? 0,
          openAmount: result?.openAmount ?? Math.max(0, openAmount - amount),
          isFullyPaid: result?.isFullyPaid ?? amount >= openAmount,
        });

        // Close modal
        onClose();

        // Refresh route data
        if (onRefreshRoute) {
          await onRefreshRoute();
        }
      } else if (response.errorCode === 'AMOUNT_EXCEEDS_OPEN') {
        // Volgens SF staat er minder open dan het ingevoerde bedrag — meestal
        // is de betaling dan al geregistreerd en toont de app verouderde data.
        // Ververs de route zodat de kaart de actuele betaalstatus laat zien.
        setError(
          'Volgens Salesforce staat er minder open dan dit bedrag. ' +
          'Waarschijnlijk is de betaling al geregistreerd. De gegevens worden ververst — ' +
          'controleer de betaalstatus op de kaart.'
        );
        if (onRefreshRoute) {
          await onRefreshRoute();
        }
      } else {
        setError(response.message || 'Betaling mislukt');
      }
    } catch (err) {
      console.error('Error processing payment:', err);
      setError('Er is iets misgegaan bij het verwerken van de betaling');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <WbModal
      open={isOpen}
      onClose={onClose}
      titel="Betaling registreren"
      subtitel={`${workOrderName} · ${customerName}`}
      sluitOpAchtergrond={!isProcessing}
      acties={
        <>
          <WbButton variant="secondary" size="lg" onClick={onClose} disabled={isProcessing}>Annuleren</WbButton>
          <WbButton size="lg" onClick={handleProcessPayment} disabled={!paymentAmount} isLoading={isProcessing}>Bevestigen</WbButton>
        </>
      }
    >
      <div className="space-y-4">
        {/* Openstaand: rood, mono groot, want dit is geld dat nog binnen moet */}
        <WbFlag kind="geld" title="Nog te betalen">
          <span className="font-mono text-3xl font-semibold tabular-nums">{euro(openAmount)}</span>
        </WbFlag>

        <WbFactRow
          facts={[
            {
              label: 'Ontvangen bedrag',
              children: (
                <input
                  type="text"
                  inputMode="decimal"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  placeholder="0,00"
                  data-wb-autofocus
                  className="wb-focus w-full bg-transparent font-mono text-2xl font-semibold text-wb-inkt placeholder:text-wb-inkt3 outline-none"
                />
              ),
            },
          ]}
        />
        <WbButton variant="link" onClick={() => setPaymentAmount(openAmount.toFixed(2))}>
          Volledig ({euro(openAmount)})
        </WbButton>

        <WbToggleGroup<PaymentMethod>
          label="Betaalmethode"
          options={[
            { value: 'Pin', label: 'Pin' },
            { value: 'Contant', label: 'Contant' },
          ]}
          value={paymentMethod}
          onChange={setPaymentMethod}
        />

        {error && <WbFlag kind="opmerking" title="Let op">{error}</WbFlag>}
      </div>
    </WbModal>
  );
}
